import { useEffect, useRef, useState } from "react";
import { MessageSquare, Bot, ListChecks, Database, UserRound, Play, Pause, RotateCcw, Video } from "lucide-react";
import PhoneMockup from "@/components/whatsapp/PhoneMockup";
import { trackEvent } from "@/lib/tracking";

const VIDEO_URL = "/media/wa-demo.mp4";
const POSTER_URL = "/media/wa-demo-poster.jpg";

const STEPS = [
  { label: "Customer enquiry", Icon: MessageSquare, note: "A customer sends a message to your WhatsApp number.",
    msgs: [{ from: "user", text: "Hi, I'd like a quotation for your services." }] },
  { label: "Automated response", Icon: Bot, note: "The WhatsApp agent replies straight away and offers clear options.",
    msgs: [{ from: "bot", text: "Welcome. I can help with your enquiry. What service are you interested in?", options: ["Installation", "Maintenance", "Something else"] }] },
  { label: "Qualification", Icon: ListChecks, note: "The agent collects name, requirement, business details and preferred contact method.",
    msgs: [{ from: "user", text: "Installation" }, { from: "bot", text: "Great. May I have your name and business name?" }, { from: "user", text: "Lerato, Bright Interiors" }, { from: "bot", text: "How would you like us to contact you?", options: ["WhatsApp", "Phone call", "Email"] }] },
  { label: "Lead capture", Icon: Database, note: "The details are recorded in the configured lead-management system.",
    msgs: [{ from: "system", text: "Lead saved · Lerato · Installation · Prefers WhatsApp" }] },
  { label: "Human handover", Icon: UserRound, note: "The conversation is passed to the right team member to continue.",
    msgs: [{ from: "bot", text: "Thank you, Lerato. A member of our team will continue this conversation with you shortly." }, { from: "system", text: "Assigned to Sales team" }] },
];

function useInView(ref) {
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const obs = new IntersectionObserver(([e]) => setInView(e.isIntersecting), { threshold: 0.25 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [ref]);
  return inView;
}

function DemoVideo() {
  const [open, setOpen] = useState(false);
  if (!open) {
    return (
      <button
        type="button"
        data-testid="wa-demo-video-play"
        onClick={() => { setOpen(true); trackEvent("demo_video_play", { service: "whatsapp" }); }}
        className="inline-flex items-center gap-2 rounded-full border border-zinc-300 bg-white px-5 h-11 text-sm font-semibold text-zinc-800 hover:border-zinc-400 transition-colors"
      >
        <Video className="h-4 w-4 text-[#25d366]" /> Watch the Chatfuel walkthrough video (~45 sec)
      </button>
    );
  }
  return (
    <div className="rounded-3xl border border-zinc-200 bg-black overflow-hidden" style={{ aspectRatio: "16 / 9" }}>
      <video data-testid="wa-demo-video" src={VIDEO_URL} poster={POSTER_URL} className="h-full w-full object-contain" controls autoPlay playsInline preload="none" />
    </div>
  );
}

export default function WhatsappDemo() {
  const ref = useRef(null);
  const inView = useInView(ref);
  const [step, setStep] = useState(0);
  const [playing, setPlaying] = useState(true);
  const opened = useRef(false);

  useEffect(() => {
    if (inView && !opened.current) {
      opened.current = true;
      trackEvent("demo_section_view", { service: "whatsapp" });
    }
  }, [inView]);

  useEffect(() => {
    const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (!inView || !playing || reduced || step >= STEPS.length - 1) return;
    const t = setTimeout(() => setStep((s) => s + 1), 2800);
    return () => clearTimeout(t);
  }, [inView, playing, step]);

  const select = (i) => { setStep(i); setPlaying(false); trackEvent("demo_step_select", { step: i + 1, service: "whatsapp" }); };
  const replay = () => { setStep(0); setPlaying(true); };
  const messages = STEPS.slice(0, step + 1).flatMap((s) => s.msgs);
  const done = step === STEPS.length - 1;

  return (
    <section ref={ref} id="demo" data-testid="wa-demo-video-section" className="relative py-24 sm:py-32 bg-sky-wash overflow-hidden">
      <div className="relative mx-auto max-w-6xl px-5 sm:px-8">
        <div className="max-w-3xl mb-12">
          <div className="text-[11px] tracking-[0.2em] uppercase text-zinc-500 font-mono mb-4">WhatsApp chatbot for business · Live demonstration</div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-[-0.03em] leading-[1.05] text-zinc-900">
            See How WhatsApp Automation Works
          </h2>
          <p className="mt-5 text-zinc-600 text-base sm:text-lg leading-relaxed">
            See how a business can use WhatsApp to respond to a customer, collect enquiry details and hand the conversation to the right team member.
          </p>
        </div>

        <div className="grid lg:grid-cols-[1fr_auto] gap-10 lg:gap-16 items-start">
          <div>
            <ol className="space-y-2" data-testid="wa-demo-steps">
              {STEPS.map((s, i) => {
                const active = i === step;
                const past = i < step;
                return (
                  <li key={s.label}>
                    <button
                      type="button"
                      onClick={() => select(i)}
                      data-testid={`wa-demo-step-${i + 1}`}
                      aria-current={active ? "step" : undefined}
                      className={`w-full text-left flex items-start gap-4 rounded-2xl border px-4 py-4 transition-[background-color,border-color,box-shadow] ${
                        active ? "bg-white border-[#25d366] shadow-[0_10px_30px_-15px_rgba(37,211,102,0.5)]" : "bg-white/60 border-zinc-200 hover:bg-white"
                      }`}
                    >
                      <span className={`h-9 w-9 rounded-xl flex items-center justify-center shrink-0 ${active || past ? "bg-[#25d366] text-white" : "bg-zinc-100 text-zinc-500"}`}>
                        <s.Icon className="h-4 w-4" />
                      </span>
                      <span>
                        <span className="block text-[10px] font-mono uppercase tracking-[0.18em] text-zinc-500">Step {i + 1}</span>
                        <span className="block font-display font-bold text-zinc-900">{s.label}</span>
                        {active && <span className="block mt-1 text-sm text-zinc-600 leading-relaxed">{s.note}</span>}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ol>
            <div className="mt-5 flex flex-wrap items-center gap-3">
              {done ? (
                <button type="button" onClick={replay} data-testid="wa-demo-replay" className="inline-flex items-center gap-2 rounded-full bg-zinc-900 text-white px-5 h-11 text-sm font-semibold">
                  <RotateCcw className="h-4 w-4" /> Replay demo
                </button>
              ) : (
                <button type="button" onClick={() => setPlaying((p) => !p)} data-testid="wa-demo-toggle" className="inline-flex items-center gap-2 rounded-full bg-zinc-900 text-white px-5 h-11 text-sm font-semibold">
                  {playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />} {playing ? "Pause" : "Play"}
                </button>
              )}
              <DemoVideo />
            </div>
          </div>

          <div className="mx-auto">
            <div className="text-center mb-3">
              <span data-testid="wa-demo-label" className="inline-block rounded-full bg-zinc-900 text-white px-3 py-1 text-[10px] font-mono uppercase tracking-[0.16em]">
                Demonstration · simulated conversation
              </span>
            </div>
            <PhoneMockup title="Bright Interiors" businessName="Your Business" messages={messages} size="lg" stickBottom
              statusBadge={done ? { label: "Handed to team", subtitle: "Lead captured", color: "#34a853" } : undefined} />
          </div>
        </div>
      </div>
    </section>
  );
}
